'use strict'
import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@material-ui/core'

// Time audit: stops on over-8-hr crew-days whose budgeted service time
// meaningfully exceeds the 12-month median of actual on-site time. Each row
// can apply the suggested prescribed man-hours (normal property PATCH), so
// the next Auto-Optimize run works with tighter budgets.

const short = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri' }

export default function TimeAuditModal ({ open, onClose, loading, suggestions, meta, onApply, applyingIds }) {
  const [appliedIds, setAppliedIds] = useState({})
  const rows = suggestions || []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line, #e0d8c2)', paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>Time Audit — over-budgeted stops on OT days</span>
          {meta && (
            <span style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
              {meta.bucketsAudited} OT crew-days audited · up to {Math.round(meta.totalPotentialSavingsMin)} min/fortnight recoverable
            </span>
          )}
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 14 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
            <CircularProgress size={24} style={{ color: 'var(--df-primary)' }} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)' }}>
            Nothing to trim: no stop on an overtime crew-day is budgeted meaningfully above its historical actuals.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--df-ink-3)', marginBottom: 10 }}>
              Budget = what the optimizer plans with (prescribed hours when set). Actual = raw 12-month median of on-site time.
              Applying sets prescribed man-hours to match the actual — review each before applying, then re-run Auto-Optimize.
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--df-line, #e0d8c2)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: 'var(--df-surface-2, #f4efe4)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Property</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Where</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700, textAlign: 'right' }}>Budget</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700, textAlign: 'right' }}>Actual</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700, textAlign: 'right' }}>Save</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700, textAlign: 'right' }}>Prescribed → Suggested</th>
                    <th style={{ padding: '6px 10px' }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const applied = !!appliedIds[row.aspirePropertyId]
                    const applying = !!(applyingIds || {})[row.aspirePropertyId]
                    return (
                      <tr key={row.aspirePropertyId} style={{ borderTop: '1px solid var(--df-line, #e0d8c2)', opacity: applied ? 0.55 : 1 }}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--df-ink, #1c2419)' }}>{row.propertyName}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>
                          {row.routeName || '—'} · {row.weekType}:{short[row.dayOfWeek] || row.dayOfWeek}
                          <span style={{ color: '#a4502f' }}> (+{row.bucketOtMin} OT)</span>
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.budgetedClockMin}m</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.actualClockMin}m</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: '#164a2c', fontWeight: 700 }}>−{row.potentialSavingsMin}m</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--df-ink-3)' }}>
                          {row.prescribedManHours != null ? `${row.prescribedManHours}h` : 'unset'} → {row.suggestedManHours}h
                        </td>
                        <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={applied || applying}
                            style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px' }}
                            onClick={async () => {
                              const ok = await onApply(row)
                              if (ok) setAppliedIds(prev => ({ ...prev, [row.aspirePropertyId]: true }))
                            }}
                          >
                            {applied ? 'Applied' : (applying ? 'Applying…' : 'Apply')}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line, #e0d8c2)', padding: '10px 24px' }}>
        <Button onClick={onClose} style={{ color: 'var(--df-ink-3)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
